'use client'

import React, { useEffect, useRef, useState } from 'react'
import { useInputWrapper } from '../../../hooks/useInputWrapper'
import { ChangeHandler } from 'react-hook-form'
import {
    isCharacterNumberOrColon,
    isNumberOrLetterKey,
} from './helpers/TimeInputHelpers'

export interface FormTimeInputProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
    disabled?: boolean
    readOnly?: boolean
    error?: boolean
    onChange: (value: string) => void
    onBlur?: ChangeHandler
    value?: string
    autoFocus?: boolean
}

export const FormTimeInput = ({
    disabled = false,
    error = false,
    readOnly = false,
    value = '',
    onChange,
    onBlur,
    autoFocus = false,
    ...rest
}: FormTimeInputProps) => {
    const { onFocusHandler, onBlurHandler, classes } = useInputWrapper(false, disabled, error)
    const [time, setTime] = useState<string>('')
    const inputRef = useRef<HTMLInputElement>(null)

    const changeHandler = (value = '') => {
        setTime(formatInputToValidTime(value))
        onChange(formatInputToValidTime(value))
    }

    /**
     * Prevents entering of unwanted characters in the time input
     */
    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const keyValue = event.key

        if (isNumberOrLetterKey(keyValue) && !isCharacterNumberOrColon(keyValue)) {
            event.preventDefault()
        }
    }

    /**
     * Ensures that upon focusing in the time input field the current input is selected
     */
    const handleFocus = () => {
        if (inputRef.current) {
            inputRef.current.select()
        }
        onFocusHandler()
    }

    const formatInputToValidTime = (input: string): string => {
        // Extract the valid parts for hours and minutes
        let hours = Math.min(parseInt(input.substring(0, 2), 10), 23)
        let minutes = isNaN(Number(input[2]))
            ? Math.min(parseInt(input.substring(3, 5), 10), 59)
            : Math.min(parseInt(input.substring(2, 4), 10), 59)

        if (isNaN(hours) || hours < 0) hours = 0
        if (isNaN(minutes) || minutes < 0) minutes = 0

        // Ensure hours and minutes are within valid ranges
        const validHours: string = hours.toString().padStart(2, '0')
        const validMinutes: string = minutes.toString().padStart(2, '0')

        // Return the formatted valid time
        return `${validHours}:${validMinutes}`
    }

    useEffect(() => {
        if (value !== time) setTime(value ?? '')
    }, [value])

    return (
        <div className={`flex items-center w-full ${classes} ${rest.className || ''}`} data-cy="input-time">
            <input
                {...rest}
                disabled={disabled}
                readOnly={readOnly}
                ref={inputRef}
                className="w-full px-3 py-2 density-lg:py-3 flex-1 outline-0 bg-transparent"
                type="text"
                value={time}
                onFocus={handleFocus}
                onBlur={(event) => {
                    changeHandler(event.target?.value)
                    onBlurHandler()
                }}
                onChange={(event) => setTime(event.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="hh:mm"
                autoFocus={autoFocus}
                aria-invalid={error}
                maxLength={5}
            />
        </div>
    )
}
